// src/pages/admin/CourseLectures/lectures/Lecture07ApplicationTracker.tsx
import React, { useState, useRef } from "react";
import { InfoBox, AgendaCard, TaskList } from "../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${isOpen ? "border-[#009fe3]/50" : "border-gray-200"} bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${isOpen ? "bg-blue-50" : "hover:bg-gray-50"}`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"}`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-sm font-bold ${isOpen ? "text-[#0369a1]" : "text-gray-800"}`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${isOpen ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Stat Card ─────────────────────────────────────────────────────
const StatCard: React.FC<{
  icon: string;
  label: string;
  desc: string;
  color: string;
}> = ({ icon, label, desc, color }) => (
  <div className={`rounded-xl border p-3 ${color}`}>
    <div className="flex items-center gap-2 mb-1">
      <span className="text-lg">{icon}</span>
      <span className="text-sm font-bold">{label}</span>
    </div>
    <p className="text-xs opacity-80">{desc}</p>
  </div>
);

// ── Field Row ─────────────────────────────────────────────────────
const FieldRow: React.FC<{
  icon: string;
  label: string;
  desc: string;
  example?: string;
  highlight?: boolean;
}> = ({ icon, label, desc, example, highlight }) => (
  <div
    className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${highlight ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"}`}
  >
    <span className="text-base flex-shrink-0 mt-0.5">{icon}</span>
    <div className="flex-1 min-w-0">
      <p
        className={`text-sm font-bold ${highlight ? "text-amber-800" : "text-gray-800"}`}
      >
        {label}
      </p>
      <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
      {example && (
        <code className="inline-block mt-1 text-[11px] bg-white border border-gray-200 rounded px-1.5 py-0.5 text-[#0369a1] font-mono">
          {example}
        </code>
      )}
    </div>
  </div>
);

// ── Follow-up Timeline ────────────────────────────────────────────
const FollowUpTimeline: React.FC = () => (
  <div className="relative pl-6 space-y-3">
    <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#009fe3] via-amber-400 to-red-400 rounded-full" />
    {[
      {
        day: "Tag 0",
        label: "Bewerbung abgeschickt",
        desc: "Im Tracker eingetragen — Next Action Due in 14 Tagen setzen",
        color: "bg-blue-50 border-blue-200 text-blue-800",
        dot: "bg-[#009fe3]",
      },
      {
        day: "Tag 14",
        label: "Follow-up per E-Mail",
        desc: "Keine Antwort erhalten? Höfliche Follow-up-E-Mail senden — Referenz angeben, Status erfragen",
        color: "bg-amber-50 border-amber-200 text-amber-800",
        dot: "bg-amber-400",
      },
      {
        day: "Tag 21+",
        label: "Follow-up per Telefon",
        desc: "Keine Antwort auf die E-Mail? Jetzt anrufen — Skype, Mobilvoip oder Brilliant App nutzen",
        color: "bg-red-50 border-red-200 text-red-800",
        dot: "bg-red-400",
      },
    ].map((item, i) => (
      <div key={i} className="flex items-start gap-3">
        <div
          className={`absolute left-0.5 w-3 h-3 rounded-full border-2 border-white flex-shrink-0 ${item.dot}`}
          style={{ top: `${i * 5.5 + 0.5}rem` }}
        />
        <div className={`flex-1 rounded-xl border px-3 py-2.5 ${item.color}`}>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wide opacity-60">
              {item.day}
            </span>
          </div>
          <p className="text-sm font-bold">{item.label}</p>
          <p className="text-xs opacity-80 mt-0.5">{item.desc}</p>
        </div>
      </div>
    ))}
  </div>
);

// ── Merge Steps ───────────────────────────────────────────────────
const MergeSteps: React.FC = () => (
  <div className="space-y-2">
    {[
      {
        step: "1",
        icon: "🌐",
        label: "Stellenausschreibung als PDF speichern",
        desc: 'Im Browser: Strg + P → "Als PDF speichern" wählen',
        cls: "bg-blue-50 border-blue-200 text-blue-800",
      },
      {
        step: "2",
        icon: "📄",
        label: "Anschreiben + Lebenslauf bereitstellen",
        desc: "Beide Dokumente als fertige PDFs vorbereiten",
        cls: "bg-purple-50 border-purple-200 text-purple-800",
      },
      {
        step: "3",
        icon: "🔗",
        label: "Alle drei PDFs zusammenführen",
        desc: "Anschreiben + Lebenslauf + Stellenausschreibung in einem PDF mergen",
        cls: "bg-green-50 border-green-200 text-green-800",
      },
      {
        step: "4",
        icon: "📦",
        label: "Komprimieren falls nötig",
        desc: "Ziel: unter 2 MB — bei Bedarf komprimieren",
        cls: "bg-amber-50 border-amber-200 text-amber-800",
      },
    ].map((item) => (
      <div
        key={item.step}
        className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${item.cls}`}
      >
        <div className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center text-sm font-extrabold flex-shrink-0">
          {item.step}
        </div>
        <div>
          <p className="text-sm font-bold">
            {item.icon} {item.label}
          </p>
          <p className="text-xs opacity-80 mt-0.5">{item.desc}</p>
        </div>
      </div>
    ))}
    <div className="bg-gray-900 rounded-xl px-4 py-3 text-xs mt-1">
      <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-2">
        Empfohlene Tools:
      </p>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-gray-500">🔗</span>
          <a
            href="https://www.ilovepdf.com/merge_pdf"
            target="_blank"
            rel="noreferrer"
            className="text-[#009fe3] underline font-semibold"
          >
            ilovepdf.com/merge_pdf
          </a>
          <span className="text-gray-500">— PDFs zusammenführen</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">🗜️</span>
          <a
            href="https://www.ilovepdf.com/compress_pdf"
            target="_blank"
            rel="noreferrer"
            className="text-[#009fe3] underline font-semibold"
          >
            ilovepdf.com/compress_pdf
          </a>
          <span className="text-gray-500">— PDF-Größe reduzieren</span>
        </div>
        <p className="text-gray-500 text-[10px] mt-1">
          Alternativ: Google → "Merge PDF" oder "Compress PDF" | smallpdf.com
        </p>
      </div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const Lecture07ApplicationTracker: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Recap */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
          📋 Aufgaben aus Lektion 06 — erledigt?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            "Erste Bewerbung eingereicht (Portal oder E-Mail)",
            "Fokus auf B2-Sprachniveau & Sprachzeugnis",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-xs text-amber-800"
            >
              <span className="w-4 h-4 rounded border border-amber-400 flex-shrink-0 bg-white" />
              {t}
            </div>
          ))}
        </div>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "Overview — Was ist der Application Tracker?" },
          { num: "2", label: "Overview — Funktionen im Dashboard" },
          { num: "3", label: "How To — Felder (Teil 1)" },
          { num: "4", label: "How To — Felder (Teil 2) & PDF-Upload" },
          { num: "5", label: "How To — Merge & Compress" },
          { num: "6", label: "Follow Up — Strategie & Ablauf" },
        ]}
      />

      {/* 01 — Overview: Was & Wo */}
      <Accordion
        id="01"
        num="ABSCHNITT 01"
        icon="📊"
        title="Overview — Was ist der Ausbildung Application Tracker?"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Der Application Tracker ist dein persönliches Kontrollzentrum — er
          hilft dir, den Überblick über alle laufenden Bewerbungen zu behalten.
        </p>
        <div className="space-y-3">
          <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] p-4">
            <p className="text-xs font-bold text-[#0369a1] uppercase tracking-wide mb-1">
              📍 Wo findest du ihn?
            </p>
            <p className="text-sm text-[#0369a1]">
              In deinem <strong>Student Dashboard</strong> — das Tool heißt{" "}
              <strong>Ausbildung Application Tracker</strong>.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-green-200 bg-green-50 p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-lg">👤</span>
                <span className="text-sm font-bold text-green-800">
                  Wer nutzt es?
                </span>
              </div>
              <p className="text-xs text-green-700">
                Alle Studierenden, die bereit sind zu bewerben und aktiv
                Bewerbungen einreichen.
              </p>
            </div>
            <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-lg">🔓</span>
                <span className="text-sm font-bold text-purple-800">
                  Wann bekommst du Zugang?
                </span>
              </div>
              <p className="text-xs text-purple-700">
                Nach Abschluss der Bewerbungsvorbereitung — G2G schaltet den
                Zugang frei und begleitet den Prozess.
              </p>
            </div>
          </div>
        </div>
        <InfoBox variant="info" icon="ℹ️" title="G2G begleitet dich">
          Nach der Freischaltung überprüft G2G deine eingetragenen Bewerbungen
          regelmäßig und gibt Feedback.
        </InfoBox>
      </Accordion>

      {/* 02 — Overview: Dashboard-Funktionen */}
      <Accordion
        id="02"
        num="ABSCHNITT 02"
        icon="🖥️"
        title="Overview — Funktionen im Dashboard"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Das Dashboard zeigt dir auf einen Blick, wie aktiv du bist und wo du
          stehst:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <StatCard
            icon="📈"
            label="Total Applications"
            desc="Wie viele Bewerbungen du insgesamt eingereicht hast"
            color="bg-blue-50 border-blue-200 text-blue-800"
          />
          <StatCard
            icon="📅"
            label="Today's Applications"
            desc="Ziel: Mindestens 2 Bewerbungen täglich in verschiedenen Berufsfeldern"
            color="bg-green-50 border-green-200 text-green-800"
          />
          <StatCard
            icon="🎯"
            label="Today's Target"
            desc="Zeigt an: Tagesziel erreicht (Met) oder nicht erreicht (NOT Met)"
            color="bg-purple-50 border-purple-200 text-purple-800"
          />
          <StatCard
            icon="⚠️"
            label="Missed Target Days"
            desc="Wie viele Tage du das Tagesziel von 2 Bewerbungen nicht erfüllt hast"
            color="bg-red-50 border-red-200 text-red-800"
          />
          <StatCard
            icon="➕"
            label="Add New Application"
            desc="Neue Bewerbung eintragen — laufende oder bereits abgeschickte"
            color="bg-amber-50 border-amber-200 text-amber-800"
          />
          <StatCard
            icon="🔍"
            label="Filter"
            desc="Nach Status filtern oder nach Keyword suchen (Firma, Position, Kontakt etc.)"
            color="bg-gray-50 border-gray-200 text-gray-800"
          />
        </div>
        <InfoBox variant="tip" icon="💡" title="Das Tagesziel">
          2 Bewerbungen pro Tag in <strong>verschiedenen Berufen</strong> — das
          erhöht deine Chancen und hält dich im Rhythmus.
        </InfoBox>
      </Accordion>

      {/* 03 — How To: Felder Teil 1 */}
      <Accordion
        id="03"
        num="ABSCHNITT 03"
        icon="✏️"
        title="How To — Felder ausfüllen (Teil 1)"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          So trägst du eine neue Bewerbung korrekt ein:
        </p>
        <div className="space-y-2">
          <FieldRow
            icon="🏢"
            label="Company Name"
            desc="Der genaue Name des Unternehmens, bei dem du dich beworben hast"
            example="Charité Berlin · Werkzeug GmbH"
          />
          <FieldRow
            icon="💼"
            label="Position / Program"
            desc="Die Stelle oder das Ausbildungsprogramm, für das du dich beworben hast"
            example="Ausbildung als Pflegefachmann · Ausbildung als Verkäufer"
          />
          <FieldRow
            icon="📅"
            label="Date Applied"
            desc="Das Datum, an dem du die Bewerbung abgeschickt hast"
            example="19.11.2025 (Format: DD.MM.YYYY)"
          />
          <FieldRow
            icon="🔄"
            label="Application Status"
            desc="In welchem Stadium befindet sich deine Bewerbung gerade? Aus Dropdown auswählen."
            example="Beworben · Einladung · Abgelehnt · Warteliste ..."
          />
          <FieldRow
            icon="📬"
            label="Feedback Received"
            desc="Hast du bereits eine Antwort vom Unternehmen erhalten? Ja oder Nein."
          />
        </div>
      </Accordion>

      {/* 04 — How To: Felder Teil 2 */}
      <Accordion
        id="04"
        num="ABSCHNITT 04"
        icon="📎"
        title="How To — Felder ausfüllen (Teil 2) & PDF-Upload"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="space-y-2 mb-4">
          <FieldRow
            icon="⏰"
            label="Next Action"
            desc="Was musst du als nächstes tun? Für jeden Schritt 14 Tage als Parameter setzen."
            example="Follow-up E-Mail senden · Anrufen · Auf Antwort warten"
          />
          <FieldRow
            icon="📆"
            label="Next Action Due"
            desc="Bis wann muss die nächste Aktion erledigt sein?"
            example="25.11.2025 → Follow-up E-Mail senden"
          />
          <FieldRow
            icon="👤"
            label="Contact Person"
            desc="Name des Ansprechpartners beim Unternehmen (Ansprechpartner)"
            example="Reinhart Müller"
          />
          <FieldRow
            icon="📧"
            label="Contact Email"
            desc="E-Mail-Adresse des Recruiters oder der HR-Abteilung"
            example="hr@company.de"
          />
          <FieldRow
            icon="📝"
            label="Notes"
            desc="Zusätzliche Notizen — z.B. Telefonnummer, besondere Anforderungen, Gesprächsnotizen"
          />
          <FieldRow
            icon="📦"
            label="CV + Cover Letter + Anzeige (PDF, max. 2 MB)"
            desc="Lebenslauf + Anschreiben + Stellenausschreibung als ein zusammengeführtes PDF hochladen. Bei Bedarf komprimieren."
            highlight
          />
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <p className="text-sm font-bold text-red-800 flex items-center gap-2">
            <span>💾</span> DO NOT forget to SAVE!
          </p>
          <p className="text-xs text-red-700 mt-1">
            Nach dem Ausfüllen aller Felder unbedingt speichern — sonst gehen
            deine Daten verloren.
          </p>
        </div>
      </Accordion>

      {/* 05 — Merge & Compress */}
      <Accordion
        id="05"
        num="ABSCHNITT 05"
        icon="🔗"
        title="How To — PDF zusammenführen & komprimieren"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Für den Upload brauchst du ein einziges PDF mit allen drei Dokumenten
          — so bereitest du es vor:
        </p>
        <MergeSteps />
      </Accordion>

      {/* 06 — Follow Up */}
      <Accordion
        id="06"
        num="ABSCHNITT 06"
        icon="📞"
        title="Follow Up — Strategie & Ablauf"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Eine Bewerbung abschicken ist erst der Anfang. Follow-up zeigt
          Initiative und erhöht deine Chancen deutlich.
        </p>

        <FollowUpTimeline />

        <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-sm font-bold text-blue-800 mb-3">
            📋 Wie du richtig nachfragst
          </p>
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-sm text-blue-700">
              <span className="flex-shrink-0 font-bold text-green-600 mt-0.5">
                ↗
              </span>
              <span>
                <strong>Angeben (Give):</strong> Bewerbungsreferenz — wann du
                dich beworben hast, für welche Stelle
              </span>
            </div>
            <div className="flex items-start gap-2 text-sm text-blue-700">
              <span className="flex-shrink-0 font-bold text-[#009fe3] mt-0.5">
                ↙
              </span>
              <span>
                <strong>Fragen (Ask):</strong> Aktueller Stand der Bewerbung —
                höflich und professionell
              </span>
            </div>
          </div>
        </div>

        <div className="mt-3 rounded-xl border border-purple-200 bg-purple-50 p-4">
          <p className="text-sm font-bold text-purple-800 mb-2">
            📱 Apps zum Anrufen aus dem Ausland
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              {
                name: "Skype",
                color: "bg-blue-100 text-blue-800 border-blue-200",
              },
              {
                name: "Mobilvoip",
                color: "bg-orange-100 text-orange-800 border-orange-200",
              },
              {
                name: "Brilliant App",
                color: "bg-green-100 text-green-800 border-green-200",
              },
            ].map((app) => (
              <span
                key={app.name}
                className={`text-xs font-bold px-2.5 py-1 rounded-full border ${app.color}`}
              >
                {app.name}
              </span>
            ))}
          </div>
          <p className="text-xs text-purple-600 mt-2 opacity-80">
            Günstig nach Deutschland telefonieren — ideal für Follow-up-Anrufe
            aus Bangladesh oder dem Ausland
          </p>
        </div>

        <InfoBox variant="tip" icon="⏱️" title="14-Tage-Regel">
          Setze für jede Bewerbung automatisch ein Follow-up-Datum auf{" "}
          <strong>14 Tage nach dem Absenden</strong>. Kein manuelles Erinnern —
          der Tracker zeigt dir, wann Aktion nötig ist.
        </InfoBox>
      </Accordion>

      {/* Fazit */}
      <Accordion
        id="fazit"
        num="FAZIT"
        icon="🏁"
        title="Fazit"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-green-800 mb-2">
              Mit dem Application Tracker behältst du die Kontrolle über deinen
              gesamten Bewerbungsprozess.
            </p>
            <ul className="space-y-1">
              {[
                "Tracker im Student Dashboard — nach Freischaltung durch G2G",
                "Tagesziel: Mindestens 2 Bewerbungen in verschiedenen Berufen",
                "Alle Felder vollständig ausfüllen — besonders Next Action + Due Date",
                "Ein zusammengeführtes PDF hochladen (LL + Anschreiben + Anzeige, max. 2 MB)",
                "14-Tage-Regel: Follow-up per E-Mail, dann per Telefon",
                "Beim Follow-up: Referenz angeben, Status höflich erfragen",
                "Zum Anrufen: Skype, Mobilvoip oder Brilliant App nutzen",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Aufgaben */}
      <Accordion
        id="aufgaben"
        num="AUFGABEN"
        icon="📋"
        title="Deine Aufgaben — Lektion 07"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              <>
                <strong>Bewerbung Protokollieren!</strong> — Jede abgeschickte
                Bewerbung sofort im Tracker eintragen
              </>,
              <>
                <strong>Mindestens 2 Bewerbungen am Tag</strong> — in
                verschiedenen Berufsfeldern
              </>,
              "Fokus auf B2-Sprachniveau sowie auf das Sprachzeugnis",
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default Lecture07ApplicationTracker;
